import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography, Avatar } from '@mui/material';
import { motion, AnimatePresence } from 'motion/react';
import { useSwipeable } from 'react-swipeable';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { RandomAvatar } from 'react-random-avatar';
import axios from 'axios';
import { getApiUrl } from '../apiUrl';

// Helper to format helpfulCount (e.g., 4200 -> "4.2K", 150 -> "150", 0 -> "0")
const formatHelpfulCount = (count) => {
  if (count === undefined || count === null) return "0";
  if (count === 0) return "0";
  if (count >= 1000) {
    const k = (count / 1000).toFixed(1);
    return k.endsWith('.0') ? `${k.slice(0, -2)}K` : `${k}K`;
  }
  return count.toString();
};

const EventImageSlideshow = ({ photos, initialIndex = 0, getImageUrl, onClose, eventId, event, description, getUserAvatar }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [userHeartCounts, setUserHeartCounts] = useState({});
  const [isHearted, setIsHearted] = useState(false);
  const [heartCount, setHeartCount] = useState(0);
  const containerRef = useRef(null);

  const API_URL = getApiUrl();

  // Initialize heart counts from photo user data and check heart status
  useEffect(() => {
    if (!photos || photos.length === 0) return;

    // Build user heart counts from photo data
    const counts = {};
    photos.forEach(photo => {
      if (photo.userId) {
        const userId = photo.userId._id 
          ? photo.userId._id.toString() 
          : photo.userId.toString();
        // Get heartsReceived from populated user data
        const hearts = photo.userId.heartsReceived || 0;
        if (!counts[userId]) {
          counts[userId] = hearts;
        }
      }
    });
    setUserHeartCounts(counts);

    // Check if current user has hearted (from event data)
    if (event && event.heartedBy) {
      checkHeartStatus();
    }
  }, [photos, event]);

  const checkHeartStatus = async () => {
    try {
      // Get current user from session
      const response = await axios.get(`${API_URL}/check-session`, { withCredentials: true });
      if (response.data.isLoggedIn && event && event.heartedBy) {
        const currentUserId = response.data._id;
        const hasHearted = event.heartedBy.some(
          heart => heart.userId && heart.userId.toString() === currentUserId
        );
        setIsHearted(hasHearted);
        setHeartCount(event.heartedBy.length);
      }
    } catch (error) {
      console.error('Error checking heart status:', error);
    }
  };

  const handleHeart = async () => {
    if (!eventId) return;
    
    if (isHearted) {
      // Can't un-heart per requirements
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/events/${eventId}/heart`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        setIsHearted(true);
        setHeartCount(response.data.heartCount || 0);
        
        // Update heart count for all photo contributors
        const counts = { ...userHeartCounts };
        photos.forEach(photo => {
          if (photo.userId) {
            const userId = photo.userId._id 
              ? photo.userId._id.toString() 
              : photo.userId.toString();
            counts[userId] = (counts[userId] || 0) + 1;
          }
        });
        setUserHeartCounts(counts);
      }
    } catch (error) {
      console.error('Error hearting:', error);
    }
  };

  const currentPhoto = photos[currentIndex];
  const currentUserId = currentPhoto?.userId?._id 
    ? currentPhoto.userId._id.toString() 
    : (currentPhoto?.userId ? currentPhoto.userId.toString() : null);
  const currentUsername = currentPhoto?.userId?.username || currentPhoto?.username || 'Unknown';
  const currentUserHeartCount = currentUserId ? (userHeartCounts[currentUserId] || 0) : 0;
  const currentUserHelpfulCount = currentPhoto?.userId?.helpfulCount || 0;

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (currentIndex < photos.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    },
    onSwipedRight: () => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    },
    trackMouse: true
  });

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, goToPrevious, goToNext, onClose]);

  if (photos.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 10004, // Above EventSheet (10003)
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        <Box
          {...handlers}
          ref={containerRef}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '100vw',
            maxHeight: '100vh'
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10000,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous button */}
          {currentIndex > 0 && (
            <IconButton
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 10000,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}

          {/* Next button */}
          {currentIndex < photos.length - 1 && (
            <IconButton
              onClick={goToNext}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 10000,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}

          {/* Image */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src={getImageUrl(currentPhoto.url)}
                alt={`Photo ${currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </motion.div>
          </AnimatePresence>

          {/* User info and heart button at bottom */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 10000
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getUserAvatar ? getUserAvatar(currentPhoto?.userId, 32) : (
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#B5504D' }}>
                  {currentUsername.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                    {currentUsername}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#a5d6a7', // Lighter pastel green for dark background
                      fontWeight: 400,
                      fontSize: '0.75rem',
                      ml: 0.25
                    }}
                  >
                    ({formatHelpfulCount(currentUserHelpfulCount)})
                  </Typography>
                </Box>
                {description && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5, maxWidth: '300px' }}>
                    {description}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleHeart}
                disabled={isHearted}
                sx={{
                  color: isHearted ? '#f44336' : 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <FavoriteIcon />
              </IconButton>
              {heartCount > 0 && (
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {heartCount}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Image counter */}
          {photos.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                zIndex: 10000
              }}
            >
              <Typography variant="caption" sx={{ color: 'white' }}>
                {currentIndex + 1} / {photos.length}
              </Typography>
            </Box>
          )}
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

export default EventImageSlideshow;

